import { call, cancelled, put, select, takeEvery } from 'redux-saga/effects';
import { getAllCookies as getStoreCookies, getProductsCompareOrchestratorHost } from 'common/selectors';
import { api } from 'api';
import { CompareCategory, getCompareCategory, getProductId } from 'features/product-info';
import { getAllowComparisons } from 'features/page-model';
import { CancelTokenSource } from '@okapi/http';
import { PRODUCTS_COMPARE_LISTS, SEND_PRODUCTS_COMPARE_LIST_ACTION } from './actions';
import { createList, deleteList, getLists, putList } from './api';
import { getProductsCompareLists } from './selectors';
import { CompareListsActionGetProps, CompareLists, CompareList, Actions } from '../types';
import { CUSTOMER_ID_COOKIE_NAME, YM_UID_COOKIE_NAME } from './constants';
import { getCookie as getClientSideCookie } from 'utils';

function* handleError(error: unknown, options: { showError?: boolean } = {}) {
  const err = error instanceof Error ? error : new Error('Unknown error');

  if (options.showError) {
    yield put(PRODUCTS_COMPARE_LISTS.failure(err));
  } else {
    // eslint-disable-next-line no-console
    console.error('[CompareListsSaga] Operation failed:', err.message);
  }
}

function getCustomerId(cookies: Record<string, string>): string | null {
  return cookies[CUSTOMER_ID_COOKIE_NAME] || cookies[YM_UID_COOKIE_NAME] || null;
}

export function* fetchCompareLists(
  { customerId, host, abortController }: CompareListsActionGetProps,
  options: { showError?: boolean } = {},
) {
  yield put(PRODUCTS_COMPARE_LISTS.request());
  try {
    const data: CompareLists = yield call(getLists, customerId, host, abortController.token);

    if (Array.isArray(data)) {
      yield put(PRODUCTS_COMPARE_LISTS.success(data));
      return true;
    } else {
      return false;
    }
  } catch (e) {
    yield* handleError(e, options);
    return false;
  }
}

export function* createListSaga({
  customerId,
  list,
  host,
  abortController,
  currentLists,
}: {
  customerId: string;
  list: CompareList;
  host: string;
  abortController: CancelTokenSource;
  currentLists: CompareLists;
}) {
  try {
    yield call(createList, { customerId, compareList: list }, host, abortController.token);
    yield put(PRODUCTS_COMPARE_LISTS.success([...currentLists, list]));
  } catch (e) {
    yield* handleError(e, { showError: true });
  } finally {
    if ((yield cancelled()) as boolean) abortController.cancel();
  }
}

export function* updateListSaga({
  customerId,
  list,
  host,
  abortController,
  currentLists,
}: {
  customerId: string;
  list: CompareList;
  host: string;
  abortController: CancelTokenSource;
  currentLists: CompareLists;
}) {
  try {
    yield call(putList, { customerId, compareList: list }, host, abortController.token);
    yield put(
      PRODUCTS_COMPARE_LISTS.success(
        currentLists.map((item) => (item.groupId === list.groupId ? list : item)),
      ),
    );
  } catch (e) {
    yield* handleError(e, { showError: true });
  } finally {
    if ((yield cancelled()) as boolean) abortController.cancel();
  }
}

export function* deleteListSaga({
  customerId,
  groupId,
  host,
  abortController,
  currentLists,
}: {
  customerId: string;
  groupId: string | null;
  host: string;
  abortController: CancelTokenSource;
  currentLists: CompareLists;
}) {
  try {
    yield call(deleteList, customerId, groupId, host, abortController.token);
    yield put(PRODUCTS_COMPARE_LISTS.success(currentLists.filter((list) => list.groupId !== groupId)));
  } catch (e) {
    yield* handleError(e, { showError: true });
  } finally {
    if ((yield cancelled()) as boolean) abortController.cancel();
  }
}

export function* handleListModification(action: { payload: { action: Actions } }) {
  try {
    const serverSideCookies: Record<string, string> = yield select(getStoreCookies);
    const customerIdServerCookie = getCustomerId(serverSideCookies);
    const customerIdClientCookie =
      getClientSideCookie(CUSTOMER_ID_COOKIE_NAME) || getClientSideCookie(YM_UID_COOKIE_NAME);
    const customerId = customerIdServerCookie || customerIdClientCookie;
    const productId: string = yield select(getProductId);

    if (!customerId) {
      throw new Error('[CompareListsSaga] customerId is required');
    }

    if (!productId) {
      throw new Error('[CompareListsSaga] productId is required');
    }

    const host: string = yield select(getProductsCompareOrchestratorHost);
    const abortController: CancelTokenSource = yield call([api, 'abortController']);

    const successGet: boolean = yield* fetchCompareLists(
      { customerId, host, abortController },
      { showError: true },
    );

    if (!successGet) return;

    const currentLists: CompareLists = yield select(getProductsCompareLists);
    const compareCategory: CompareCategory = yield select(getCompareCategory);
    const { familyId: groupId, name } = compareCategory;

    const existingList = currentLists.find((list) => list.groupId === groupId);
    const products = new Set(existingList?.products || []);

    if (action.payload.action === 'add') {
      products.add(productId);
    } else if (action.payload.action === 'delete') {
      products.delete(productId);
    }

    const modifiedList = {
      name,
      groupId,
      products: Array.from(products),
    };

    if (!existingList) {
      yield* createListSaga({ customerId, list: modifiedList, host, abortController, currentLists });
    } else if (modifiedList.products.length === 0) {
      yield* deleteListSaga({ customerId, groupId, host, abortController, currentLists });
    } else {
      yield* updateListSaga({ customerId, list: modifiedList, host, abortController, currentLists });
    }
  } catch (error) {
    yield handleError(error, { showError: true });
  }
}

export function* fetchCompareListsOnServer() {
  const cookies: Record<string, string> = yield select(getStoreCookies);
  const customerId = getCustomerId(cookies);
  const allowComparison: boolean = yield select(getAllowComparisons);

  if (!customerId || !allowComparison) return;

  const host: string = yield select(getProductsCompareOrchestratorHost);
  const abortController: CancelTokenSource = yield call([api, 'abortController']);
  const success = yield* fetchCompareLists({ customerId, host, abortController }, { showError: false });
  if (!success) return;
}

export function* watchCompareListActions() {
  yield takeEvery(SEND_PRODUCTS_COMPARE_LIST_ACTION, handleListModification);
}
